/**
 * Portal Public API Client (Story 14.3 — Task 7.2 / AC #12)
 *
 * Minimal raw-fetch helpers for the public portal viewer. KEPT SEPARATE from
 * `./apiClient.ts` for two reasons:
 *
 *  1. The authenticated `apiClient` transparently attempts a 401 refresh-token
 *     flow on every failure. The portal has no access token and no refresh
 *     cookie — triggering that flow would redirect the viewer to /login.
 *
 *  2. The public portal must NEVER attach an `Authorization` header (which
 *     would be `Bearer undefined` or leak a stale agency token if the same
 *     browser is also logged into ProvenLeap). Isolating fetchers removes
 *     that foot-gun entirely.
 *
 * Cookie handling: `credentials: "include"` is required so the HMAC-signed
 * `provenleap_portal_session` cookie is round-tripped between verify-password
 * and subsequent /view calls. Browsers enforce SameSite=Lax on our side, so
 * third-party sites cannot ride this cookie.
 *
 * Timeout: 10 seconds via `AbortSignal.timeout(10_000)` — matches the backend's
 * per-platform fetch budget. A portal viewer will see a timeout error rather
 * than a pending spinner forever.
 */

import type {
  PortalMeta,
  PortalViewResponse,
  PortalMetaInactive,
  PortalVerifyPasswordResponse,
  PortalDateRangePreset,
  PortalComparisonPreset,
} from "@provenleap/types";

// ============================================================================
// Types
// ============================================================================

export interface FetchPortalViewOptions {
  dateRange?: PortalDateRangePreset;
  comparison?: PortalComparisonPreset;
  /** When true, appends `?refresh=1` to bypass the server-side 60s KV cache. */
  refresh?: boolean;
}

/** Result of a verify-password call — includes HTTP status for 429 handling. */
export interface VerifyPortalPasswordResult {
  status: number;
  body: PortalVerifyPasswordResponse | { error: string };
  /** When status === 429, parsed from the `Retry-After` header (in seconds). */
  retryAfter?: number;
}

/** Error shape surfaced to callers when fetch itself fails (network, timeout). */
export class PortalApiError extends Error {
  constructor(
    message: string,
    public readonly kind: "network" | "timeout" | "bad_response"
  ) {
    super(message);
    this.name = "PortalApiError";
  }
}

// ============================================================================
// Constants
// ============================================================================

/** Portal API fetch timeout (AC #12). 10s matches the server-side per-platform budget. */
const PORTAL_FETCH_TIMEOUT_MS = 10_000;

function buildPortalUrl(path: string): string {
  // Same-origin by design — the worker is colocated with the web app in prod.
  // Local dev: the Vite proxy forwards `/api/*` to the server worker at :8787.
  return path;
}

// ============================================================================
// Fetchers
// ============================================================================

/**
 * Fetch the meta bootstrap response for a portal token.
 *
 * Returns the full `PortalMeta` union — callers switch on `status`:
 *   - `"inactive"` → render the `<PortalInactivePage reason={reason} />`
 *   - `"active"` with `passwordProtected === true` → render password gate
 *   - `"active"` with `passwordProtected === false` → proceed to `/view`
 */
export async function fetchPortalMeta(token: string): Promise<PortalMeta> {
  // encodeURIComponent defends against future token format changes (base64url
  // happens to be URL-safe today; if entropy ever expands to include `/` or `+`
  // we don't want to rely on that assumption breaking path parsing).
  const url = buildPortalUrl(`/api/portal-view/${encodeURIComponent(token)}/meta`);
  const res = await safeFetch(url, {
    method: "GET",
    credentials: "include",
  });
  return parseJsonOrThrow<PortalMeta>(res);
}

/**
 * Submit a password for a portal link.
 *
 * Returns `{ status, body, retryAfter? }` so callers can:
 *   - `status === 200 && body.verified === true` → refresh meta / proceed
 *   - `status === 200 && body.verified === false` → show "incorrect password"
 *   - `status === 429` → show rate-limit banner with `retryAfter` countdown
 */
export async function verifyPortalPassword(
  token: string,
  password: string
): Promise<VerifyPortalPasswordResult> {
  const url = buildPortalUrl(
    `/api/portal-view/${encodeURIComponent(token)}/verify-password`
  );
  const res = await safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });

  // Parse body optimistically; 429 responses are JSON too (RFC 9457 Problem)
  let body: PortalVerifyPasswordResponse | { error: string };
  try {
    body = (await res.json()) as PortalVerifyPasswordResponse | { error: string };
  } catch {
    throw new PortalApiError(
      "Invalid JSON response from verify-password",
      "bad_response"
    );
  }

  const retryAfterHeader = res.headers.get("Retry-After");
  const retryAfter = retryAfterHeader
    ? Number.parseInt(retryAfterHeader, 10)
    : undefined;

  return {
    status: res.status,
    body,
    ...(Number.isFinite(retryAfter) ? { retryAfter } : {}),
  };
}

/**
 * Fetch portal dashboard data.
 *
 * Returns a discriminated union:
 *   - `PortalViewResponse` (status === "active") → render the shell
 *   - `PortalMetaInactive` (status === "inactive") → flip to inactive page
 *     (link became revoked/expired mid-session)
 *
 * A 401 `{ error: "password_required" }` response is thrown as a
 * `PortalApiError("password_required", "bad_response")` — callers should
 * catch this specifically and re-render the password gate.
 */
export async function fetchPortalView(
  token: string,
  options: FetchPortalViewOptions = {}
): Promise<PortalViewResponse | PortalMetaInactive> {
  const params = new URLSearchParams();
  if (options.dateRange) params.set("dateRange", options.dateRange);
  if (options.comparison) params.set("comparison", options.comparison);
  if (options.refresh) params.set("refresh", "1");

  const qs = params.toString();
  const url = buildPortalUrl(
    `/api/portal-view/${encodeURIComponent(token)}/view${qs ? "?" + qs : ""}`
  );
  const res = await safeFetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (res.status === 401) {
    // Distinct from network / parse errors: the client should re-open the
    // password gate. We throw a well-known string for component code to match.
    throw new PortalApiError("password_required", "bad_response");
  }

  return parseJsonOrThrow<PortalViewResponse | PortalMetaInactive>(res);
}

/**
 * Build the portal PDF-download URL for a report-scoped link (Story 7.11). The
 * download endpoint streams the stored report PDF as an attachment; the portal
 * token in the path IS the credential (the password-session cookie rides along
 * automatically on a same-origin navigation). Returns the URL so the caller can
 * trigger a browser download (anchor click / window.open) — fetch+blob is avoided
 * so the browser handles the Content-Disposition attachment natively.
 */
export function portalDownloadUrl(token: string): string {
  return buildPortalUrl(
    `/api/portal-view/${encodeURIComponent(token)}/download`,
  );
}

// ============================================================================
// Internal helpers
// ============================================================================

/**
 * Wrapper around `fetch` that adds a 10-second timeout via AbortSignal and
 * translates network / timeout / abort errors into `PortalApiError`.
 */
async function safeFetch(
  url: string,
  init: RequestInit
): Promise<Response> {
  try {
    return await fetch(url, {
      ...init,
      signal: AbortSignal.timeout(PORTAL_FETCH_TIMEOUT_MS),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "TimeoutError") {
      throw new PortalApiError("Request timed out", "timeout");
    }
    throw new PortalApiError(
      err instanceof Error ? err.message : "Network error",
      "network"
    );
  }
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  try {
    return (await res.json()) as T;
  } catch {
    throw new PortalApiError("Invalid JSON response", "bad_response");
  }
}
